/**
 * Steps v2 Do-next hook.
 *
 * State lives in the store (`board.steps[]`, `board.currentStepId`, and the
 * server projections `board.contextRecommendations` / `board.goalHistory`).
 * This hook owns only transient UI state (loading / error / thinking phase /
 * batch index) and the side-effect pipelines:
 *
 *   generate  — board-goal Run (store.boardGoalRefreshToken)   → POST /steps/generate
 *   patch     — refresh button / decide answer                 → POST /steps/patch
 *   fill      — current step has no chips yet (title-only)     → POST /steps/fill
 *   pin-case  — pin / AI pin / pasted evidence added or removed → digest + soft classify
 *
 * The user never types or picks a step: only generate and patch rewrite the
 * list, and the pointer otherwise moves only when a step finishes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  postBoardAiPlacePending,
  postBoardAiPinCaseAnalyze,
  postBoardAiDigestEvent,
} from '../api/boardAiApi';
import {
  buildDigestAddEvents,
  buildDigestDeleteEvents,
} from '../utils/boardConstraintsDigest';
import {
  markDigestDeleted,
  withdrawConstraintsForDeletedSources,
} from '../utils/withdrawConstraints';
import {
  buildUserDecisionConstraint,
  mergeUserDecisionConstraint,
} from '../utils/userDecisionConstraint';
import { buildClientContext } from '../utils/buildClientContext';
import { getActivityId } from '../utils/autoLayout';
import { placeDonePinInGapFrame } from '../utils/applyBoardAiActions';
import { shouldFrameDoneFill } from '../utils/pasteCanvasEvidence';
import {
  addedPinIdsSinceBaseline,
  filterLivePinCaseSources,
  gonePinCaseSources,
  hasLivePinCaseSources,
  dropAlreadyJudgedPinCaseSources,
  visitPinCaseRequest,
} from '../utils/livePinCaseSources';
import { mergeAiCostIntoMeta, normalizeAiCost } from '../utils/aiCostFormat';
import { createOpsOnlyDocumentSaver } from '../utils/persistBoardDocumentOps';
import {
  applyContextRecommendationBatch,
  applySoftProgressToLiveCoach,
  buildFallbackContextRecommendations,
  canShowMoreContextRecommendations,
  getContextRecommendationBatchCount,
  hasContextRecommendations,
  hasPersistedDoNextPack,
  normalizeContextRecommendations,
  pruneFillsForDeletedSources,
  withContextRecommendationMeta,
} from '../utils/boardContextRecommendations';
import { actionKeyForNextAction } from '../utils/linkBlockersToActions';
import { getActivityTitle } from '../utils/boardSelectionContext';
import {
  INTENT_EVENT_TYPES,
  foldBoardIntentSummary,
  getBoardRecentIntentSummary,
  pushBoardIntentEvent,
  resetBoardIntentBuffers,
  seedBoardIntentFromSummary,
  truncateIntentLabel,
} from '../utils/boardRecentIntent';
import { resolveGoalStepStatus } from '../utils/goalHistory';
import { useBoardStore } from '../store/useBoardStore';
import { readBoardGoal, readCurrentStep } from '../utils/boardGoalFields';
import { collectObjectProcessingTargets } from '../utils/objectProcessing';
import {
  boardSteps,
  currentStep as selectCurrentStep,
  findStep,
  projectStepsToRecs,
  stepChoicesFromBoard,
  stepHasPack,
} from '../utils/steps';

// ---------------------------------------------------------------------------
// Pin-case helpers (unchanged behaviour from the previous hook)
// ---------------------------------------------------------------------------

/** Write pin-case classify cost/case onto canvas evidence shapes (DEV hover). */
function stampCanvasEvidencePinCaseMeta(editor, result) {
  if (!editor || !result) return;
  const cost = normalizeAiCost(result.jobMetrics);
  const rows = Array.isArray(result.results) && result.results.length
    ? result.results
    : [result];
  const updates = [];
  for (const row of rows) {
    const shapeId = String(
      row?.softProgress?.matchedShapeId
      || row?.pinSummary?.shapeId
      || row?.pinSummary?.id
      || ''
    ).trim();
    if (!shapeId) continue;
    const shape = editor.getShape(shapeId);
    if (!shape) continue;
    const caseName = String(row?.case || result?.case || '').trim();
    updates.push({
      id: shapeId,
      type: shape.type,
      meta: {
        ...(shape.meta || {}),
        ...(caseName ? { pinCase: caseName } : {}),
        ...mergeAiCostIntoMeta(shape.meta || {}, cost),
      },
    });
  }
  if (!updates.length) return;
  try {
    editor.updateShapes(updates);
  } catch {
    // meta is best-effort
  }
}

function collectPinCaseMarks(result) {
  const rows = Array.isArray(result?.results) && result.results.length
    ? result.results
    : result
      ? [result]
      : [];
  const marks = [];
  for (const row of rows) {
    const activityId = String(
      row?.softProgress?.matchedActivityId
      || row?.pinSummary?.activityId
      || ''
    ).trim();
    const caseName = String(row?.case || result?.case || '').trim();
    if (!activityId || !caseName || caseName === 'already_filled') continue;
    marks.push({ activityId, pinCase: caseName });
  }
  return marks;
}

function applyPinCaseToLocalPlacements(result) {
  const marks = collectPinCaseMarks(result);
  if (!marks.length) return;
  const store = useBoardStore.getState();
  const placements = Array.isArray(store.board?.placements) ? store.board.placements : [];
  if (!placements.length) return;
  const byId = new Map(marks.map(mark => [mark.activityId, mark.pinCase]));
  let changed = false;
  const next = placements.map((placement) => {
    const pinCase = byId.get(String(placement?.activityId || ''));
    if (!pinCase || String(placement?.pinCase || '') === pinCase) return placement;
    changed = true;
    return { ...placement, pinCase };
  });
  if (!changed) return;
  store.updatePlacementsLocal(next);
  store.savePlacements?.(next);
}

function countAiPinsFromEditor(editor) {
  if (!editor?.getCurrentPageShapes) return 0;
  let count = 0;
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape?.type === 'suggest-pin' || shape?.type === 'conversation-pin') count += 1;
  }
  return count;
}

function countFramesFromEditor(editor, board) {
  const fromBoard = Array.isArray(board?.frames) ? board.frames.length : 0;
  if (!editor?.getCurrentPageShapes) return fromBoard;
  let count = 0;
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape?.type === 'frame') count += 1;
  }
  return Math.max(count, fromBoard);
}

function listInboxActivityIds(board) {
  return (board?.placements || [])
    .filter(p => {
      const source = String(p?.placementSource || 'inbox');
      return source === 'inbox' || source === '';
    })
    .map(p => String(p.activityId))
    .filter(Boolean);
}

function buildPinTitlesMap(activities = []) {
  const map = {};
  for (const activity of activities) {
    const id = getActivityId(activity);
    if (!id) continue;
    map[id] = truncateIntentLabel(getActivityTitle(activity) || id.slice(-6));
  }
  return map;
}

function enqueueNewPinsOnVisit(enqueuePinCase, previousPinIds, board) {
  const storeState = useBoardStore.getState();
  const currentPinIds = (storeState.activities || []).map(getActivityId);
  const { addedPinIds, newInboxIds } = visitPinCaseRequest({
    previousPinIds,
    currentPinIds,
    placements: (board || storeState.board)?.placements || [],
  });
  if (addedPinIds.length) {
    enqueuePinCase({
      activityIds: addedPinIds,
      placeActivityIds: newInboxIds,
      placePending: newInboxIds.length > 0,
      aiPins: [],
    });
  }
  return { currentPinIds, storeState, addedPinIds };
}

function emitPinDiffEvents(hashtag, previousPinIds, currentPinIds, previousTitles, currentTitles) {
  const prevSet = new Set(previousPinIds || []);
  const nextSet = new Set(currentPinIds || []);
  for (const id of nextSet) {
    if (prevSet.has(id)) continue;
    pushBoardIntentEvent(hashtag, {
      type: INTENT_EVENT_TYPES.PIN_ADDED,
      label: currentTitles[id] || id.slice(-6),
    });
  }
  for (const id of prevSet) {
    if (nextSet.has(id)) continue;
    pushBoardIntentEvent(hashtag, {
      type: INTENT_EVENT_TYPES.PIN_REMOVED,
      label: previousTitles[id] || id.slice(-6),
    });
  }
}

function emitSelectionFocusEvent(hashtag, editor, activities) {
  if (!editor?.getSelectedShapeIds) return;
  const titles = [];
  for (const shapeId of editor.getSelectedShapeIds()) {
    const shape = editor.getShape(shapeId);
    if (!shape || shape.type !== 'pin-card' || !shape.meta?.activityId) continue;
    const id = String(shape.meta.activityId);
    const activity = (activities || []).find(a => getActivityId(a) === id);
    titles.push(truncateIntentLabel(getActivityTitle(activity) || id.slice(-6)));
    if (titles.length >= 3) break;
  }
  if (!titles.length) return;
  pushBoardIntentEvent(hashtag, {
    type: INTENT_EVENT_TYPES.SELECTION_FOCUS,
    label: titles.join(', '),
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBoardSteps({
  editorRef,
  hashtag,
  board,
  pinCount,
  canvasHydrationTick = 0,
  locale = 'en',
}) {
  const updateContextRecommendationsLocal = useBoardStore(
    state => state.updateContextRecommendationsLocal
  );
  const updateRecentIntentLocal = useBoardStore(state => state.updateRecentIntentLocal);
  const persistContextRecommendations = useBoardStore(
    state => state.persistContextRecommendations
  );
  const updatePlacementsLocal = useBoardStore(state => state.updatePlacementsLocal);
  const savePlacements = useBoardStore(state => state.savePlacements);
  const boardGoalRefreshToken = useBoardStore(state => state.boardGoalRefreshToken || 0);
  const storeHashtag = useBoardStore(state => state.hashtag);
  // Coach UI must only reflect the route hashtag once the store has loaded that board.
  const boardMatchesRoute = Boolean(hashtag && storeHashtag === hashtag && board);

  const [suggestionBatchIndex, setSuggestionBatchIndex] = useState(0);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState(null);
  const [thinkingPhase, setThinkingPhase] = useState('');
  const [lastJobMetrics, setLastJobMetrics] = useState(null);
  const [pinCaseRequest, setPinCaseRequest] = useState(0);
  /** Pending AI job: { kind: 'generate'|'patch'|'fill', stepId, userDecision } */
  const [aiJob, setAiJob] = useState(null);
  const fillAttemptedRef = useRef(new Set());

  const aiJobIdRef = useRef(0);
  const pinCaseIdRef = useRef(0);
  const previousContextRef = useRef(null);
  const pendingPinCaseRef = useRef(null);
  const autoGeneratedRef = useRef(false);
  const appliedBoardGoalRefreshTokenRef = useRef(boardGoalRefreshToken);
  const boardRef = useRef(board);
  const pinCountRef = useRef(pinCount);
  const hashtagRef = useRef(hashtag);
  const localeRef = useRef(locale);
  boardRef.current = board;
  pinCountRef.current = pinCount;
  hashtagRef.current = hashtag;
  localeRef.current = locale;

  const currentStepTitle = readCurrentStep(board);
  const boardGoalTitle = readBoardGoal(board);
  const fallback = useMemo(
    () => buildFallbackContextRecommendations({
      hashtag,
      currentStep: currentStepTitle,
      boardGoal: boardGoalTitle,
      locale,
    }),
    [hashtag, currentStepTitle, boardGoalTitle, locale]
  );
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  // ---- Derived coach state -------------------------------------------------
  // The current step's chips are projected from `board.steps[]` +
  // `board.currentStepId`, plus the step-independent ledger kept on
  // `contextRecommendations`.
  const boardStepsList = board?.steps;
  const boardCurrentStepId = board?.currentStepId;
  const boardRecs = board?.contextRecommendations;
  const liveCoach = useMemo(() => {
    if (!boardMatchesRoute) return null;
    const recs = projectStepsToRecs({
      steps: boardStepsList,
      currentStepId: boardCurrentStepId,
      contextRecommendations: boardRecs,
    });
    if (!hasPersistedDoNextPack(recs)) return null;
    return normalizeContextRecommendations(recs, fallback);
  }, [boardMatchesRoute, boardStepsList, boardCurrentStepId, boardRecs, fallback]);
  const liveCoachRef = useRef(liveCoach);
  liveCoachRef.current = liveCoach;

  const contextActions = useMemo(
    () => (liveCoach ? applyContextRecommendationBatch(liveCoach, suggestionBatchIndex) : null),
    [liveCoach, suggestionBatchIndex]
  );
  const canShowMoreSuggestions = useMemo(
    () => canShowMoreContextRecommendations(liveCoach),
    [liveCoach]
  );
  const suggestionBatchCount = useMemo(
    () => getContextRecommendationBatchCount(liveCoach),
    [liveCoach]
  );
  // ---- Reset on canvas switch ----------------------------------------------
  useEffect(() => {
    previousContextRef.current = null;
    autoGeneratedRef.current = false;
    fillAttemptedRef.current = new Set();
    aiJobIdRef.current += 1;
    pinCaseIdRef.current += 1;
    useBoardStore.getState().clearObjectProcessing();
    resetBoardIntentBuffers();
    setContextError(null);
    setContextLoading(false);
    setThinkingPhase('');
    setSuggestionBatchIndex(0);
    setAiJob(null);
  }, [hashtag]);

  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || board?.recentIntent == null) return;
    seedBoardIntentFromSummary(hashtag, board.recentIntent);
  }, [hashtag, boardMatchesRoute, board?.recentIntent]);

  // ---- Client context builder ----------------------------------------------
  const buildContextForAi = useCallback(async ({ userDecision = false } = {}) => {
    const editor = editorRef.current;
    const currentBoard = boardRef.current;
    if (!editor) return null;
    const storeState = useBoardStore.getState();
    const currentPinIds = (storeState.activities || []).map(getActivityId);
    const currentPinTitles = buildPinTitlesMap(storeState.activities || []);
    if (previousContextRef.current) {
      emitPinDiffEvents(
        hashtagRef.current,
        previousContextRef.current.pinIds || [],
        currentPinIds,
        previousContextRef.current.pinTitles || {},
        currentPinTitles
      );
    }
    emitSelectionFocusEvent(hashtagRef.current, editor, storeState.activities);
    const recentIntentSummary = getBoardRecentIntentSummary(hashtagRef.current, {
      priorSummary: currentBoard?.recentIntent || '',
    });
    const context = await buildClientContext(editor, {
      board: currentBoard,
      previousContext: previousContextRef.current,
      pinCount: pinCountRef.current,
      includeSnapshot: false,
      recentIntent: recentIntentSummary,
      locale: localeRef.current,
    });
    if (!context) return null;
    const liveConstraints = currentBoard?.contextRecommendations?.constraints;
    if (Array.isArray(liveConstraints) && liveConstraints.length) {
      context.constraints = liveConstraints;
    }
    if (userDecision) context.userDecisionRefresh = true;
    previousContextRef.current = {
      pinCount: pinCountRef.current,
      aiPinCount: countAiPinsFromEditor(editor),
      pinIds: currentPinIds,
      pinTitles: currentPinTitles,
      currentStep: readCurrentStep(currentBoard),
      boardGoal: readBoardGoal(currentBoard),
      recentIntent: recentIntentSummary,
    };
    return context;
  }, [editorRef]);

  const finishAiJob = useCallback((jobId, { intent = null } = {}) => {
    if (jobId !== aiJobIdRef.current) return;
    if (intent != null) updateRecentIntentLocal(intent);
    setContextLoading(false);
    setThinkingPhase('');
  }, [updateRecentIntentLocal]);

  // ---- generate / patch runner ---------------------------------------------
  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || !aiJob || !editorRef.current) return undefined;
    const job = aiJob;
    const jobHashtag = hashtag;
    const jobId = aiJobIdRef.current + 1;
    aiJobIdRef.current = jobId;
    const isCurrent = () => (
      jobId === aiJobIdRef.current
      && hashtagRef.current === jobHashtag
      && useBoardStore.getState().hashtag === jobHashtag
    );

    setContextLoading(true);
    setContextError(null);
    setThinkingPhase('preparing');
    setSuggestionBatchIndex(0);

    const timer = setTimeout(async () => {
      try {
        if (!isCurrent()) return;
        const context = await buildContextForAi({ userDecision: Boolean(job.userDecision) });
        if (!context) throw new Error('Canvas is not ready yet');
        if (!isCurrent()) return;
        setThinkingPhase('drafting');
        const store = useBoardStore.getState();
        let result = null;
        if (job.kind === 'generate') result = await store.generateSteps(context);
        else if (job.kind === 'patch') result = await store.patchSteps(context);
        else result = await store.fillStep(job.stepId, context);
        if (!isCurrent()) return;
        if (result?.jobMetrics) setLastJobMetrics({ ...result.jobMetrics, cached: false });
        if (result?.skipped && result.reason === 'coach_failed') {
          setContextError('Could not draft steps for this goal');
        } else if (result?.skipped && result.reason === 'fill_failed') {
          setContextError('Could not draft missing parts for this step');
        } else if (result?.skipped && result.reason === 'patch_failed') {
          setContextError('Could not draft new steps from recent changes');
        }
        const folded = foldBoardIntentSummary(jobHashtag, {
          priorSummary: boardRef.current?.recentIntent || '',
        });
        finishAiJob(jobId, { intent: folded });
      } catch (err) {
        if (!isCurrent()) return;
        setContextError(err?.message || 'Could not refresh steps');
        finishAiJob(jobId);
      } finally {
        if (isCurrent()) setAiJob(null);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if (isCurrent()) {
        setContextLoading(false);
        setThinkingPhase('');
      }
    };
  }, [hashtag, boardMatchesRoute, aiJob, editorRef, buildContextForAi, finishAiJob]);

  // Board-goal Run: the store bumps boardGoalRefreshToken after the goal PATCH
  // succeeds. One generate per token.
  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || !canvasHydrationTick) return;
    if (appliedBoardGoalRefreshTokenRef.current === boardGoalRefreshToken) return;
    appliedBoardGoalRefreshTokenRef.current = boardGoalRefreshToken;
    setAiJob({ kind: 'generate' });
  }, [boardGoalRefreshToken, hashtag, boardMatchesRoute, canvasHydrationTick]);

  /**
   * Refresh button / decide flow → whole new open list from the ledger +
   * added/deleted objects. Previous open steps are only a "do not repeat" hint.
   */
  const refreshContextActions = useCallback((options = {}) => {
    setAiJob({
      kind: 'patch',
      userDecision: Boolean(options?.userDecision),
    });
  }, []);

  // Generate returns chips only for step 1; the others are title-only. When
  // the pointer lands on one (step finished, pin-case match), draft its chips
  // once per step.
  const currentStepRecord = useMemo(
    () => (boardMatchesRoute ? selectCurrentStep({ steps: boardStepsList, currentStepId: boardCurrentStepId }) : null),
    [boardMatchesRoute, boardStepsList, boardCurrentStepId]
  );
  const currentStepNeedsFill = Boolean(
    currentStepRecord
    && currentStepRecord.status === 'open'
    && !stepHasPack(currentStepRecord)
  );
  useEffect(() => {
    if (!canvasHydrationTick || aiJob || contextLoading) return;
    if (!currentStepNeedsFill) return;
    const stepId = currentStepRecord.id;
    if (fillAttemptedRef.current.has(stepId)) return;
    fillAttemptedRef.current.add(stepId);
    setAiJob({ kind: 'fill', stepId });
  }, [canvasHydrationTick, aiJob, contextLoading, currentStepNeedsFill, currentStepRecord]);

  // ---- Hydration: never auto-call AI except for a goal with no steps at all --
  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || !canvasHydrationTick) return;
    const editor = editorRef.current;
    const storeState = useBoardStore.getState();
    if (!previousContextRef.current) {
      const { currentPinIds } = enqueueNewPinsOnVisit(
        () => {},
        undefined,
        boardRef.current
      );
      previousContextRef.current = {
        pinCount: pinCountRef.current || 0,
        aiPinCount: countAiPinsFromEditor(editor),
        pinIds: currentPinIds,
        pinTitles: buildPinTitlesMap(storeState.activities || []),
        currentStep: readCurrentStep(boardRef.current),
        boardGoal: readBoardGoal(boardRef.current),
        recentIntent: boardRef.current?.recentIntent || '',
      };
      if (editor) {
        void buildClientContext(editor, {
          board: boardRef.current,
          pinCount: pinCountRef.current,
          includeSnapshot: false,
          recentIntent: boardRef.current?.recentIntent || '',
        }).then(context => {
          if (!context || hashtagRef.current !== hashtag) return;
          previousContextRef.current = {
            ...(previousContextRef.current || {}),
            viewport: context.viewport,
            selection: context.selection,
          };
        });
      }
    }
    const hasGoal = Boolean(readBoardGoal(boardRef.current).trim());
    const hasSteps = boardSteps(boardRef.current).length > 0;
    if (hasGoal && !hasSteps && !autoGeneratedRef.current) {
      autoGeneratedRef.current = true;
      setAiJob({ kind: 'generate' });
    }
  }, [hashtag, boardMatchesRoute, canvasHydrationTick, editorRef]);

  // ---- Pin-case pipeline ----------------------------------------------------
  const enqueuePinCase = useCallback((request) => {
    const nextTargets = collectObjectProcessingTargets(request);
    const prevTargets = pendingPinCaseRef.current?.processingTargets || [];
    const nextKeys = new Set(nextTargets.map(target => target.key));
    const stale = prevTargets.filter(target => !nextKeys.has(target.key));
    const store = useBoardStore.getState();
    if (stale.length) store.clearObjectProcessing(stale);

    const runId = pinCaseIdRef.current + 1;
    pinCaseIdRef.current = runId;
    const phase = request.placePending && (request.placeActivityIds || []).length
      ? 'placing'
      : 'digesting';
    if (nextTargets.length) store.setObjectProcessing(nextTargets, phase, { runId });

    pendingPinCaseRef.current = { ...request, processingTargets: nextTargets, runId };
    setPinCaseRequest(n => n + 1);
  }, []);

  // Pin count drift → classify pins added since the baseline.
  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || !canvasHydrationTick) return;
    if (!previousContextRef.current) return;
    const storeState = useBoardStore.getState();
    const currentPinIds = (storeState.activities || []).map(getActivityId);
    const addedPinIds = addedPinIdsSinceBaseline(previousContextRef.current.pinIds, currentPinIds);
    if (addedPinIds.length) {
      const inboxIds = listInboxActivityIds(boardRef.current);
      enqueuePinCase({
        activityIds: addedPinIds,
        placeActivityIds: inboxIds.filter(id => addedPinIds.includes(id)),
        placePending: inboxIds.some(id => addedPinIds.includes(id)),
        aiPins: [],
      });
    }
    previousContextRef.current = {
      ...previousContextRef.current,
      pinCount: pinCount || 0,
      aiPinCount: countAiPinsFromEditor(editorRef.current),
      pinIds: currentPinIds,
      pinTitles: buildPinTitlesMap(storeState.activities || []),
    };
  }, [hashtag, boardMatchesRoute, canvasHydrationTick, pinCount, editorRef, enqueuePinCase]);

  /**
   * After a user/AI pin or pasted canvas evidence is added: place inbox drafts
   * if needed, enqueue pendingDigest, then soft-only pin-case classify.
   */
  const analyzePinAdded = useCallback(({
    activityId = null,
    activityIds = null,
    aiPin = null,
    aiPins = null,
    canvasEvidence = null,
    canvasEvidences = null,
    placePending = true,
  } = {}) => {
    const ids = Array.isArray(activityIds)
      ? activityIds.map(String).filter(Boolean)
      : activityId
        ? [String(activityId)]
        : [];
    const pins = Array.isArray(aiPins)
      ? aiPins.filter(Boolean)
      : aiPin
        ? [aiPin]
        : [];
    const evidences = Array.isArray(canvasEvidences)
      ? canvasEvidences.filter(e => e && String(e.text || '').trim())
      : canvasEvidence && String(canvasEvidence.text || '').trim()
        ? [canvasEvidence]
        : [];
    const inboxIds = placePending !== false ? listInboxActivityIds(boardRef.current) : [];
    const toPlace = placePending === false
      ? []
      : (ids.length ? ids.filter(id => inboxIds.includes(id)) : inboxIds);

    if (!ids.length && !pins.length && !evidences.length && !toPlace.length) return;

    if (ids.length && previousContextRef.current) {
      const prevIds = previousContextRef.current.pinIds || [];
      const merged = [...new Set([...prevIds, ...ids])];
      previousContextRef.current = {
        ...previousContextRef.current,
        pinIds: merged,
        pinCount: Math.max(Number(previousContextRef.current.pinCount || 0), merged.length),
      };
    }

    enqueuePinCase({
      activityIds: ids.length ? ids : toPlace,
      aiPins: pins,
      canvasEvidences: evidences,
      placePending: placePending !== false && toPlace.length > 0,
      placeActivityIds: toPlace,
    });
  }, [enqueuePinCase]);

  /** Queue pin deletes into pendingDigest (constraints revoke on fold/patch). */
  const enqueueDigestDelete = useCallback((activityIds = [], shapeIds = []) => {
    const ids = (activityIds || []).map(String).filter(Boolean);
    const shapes = (shapeIds || []).map(String).filter(Boolean);
    if (!ids.length && !shapes.length) return;
    const tag = hashtagRef.current;
    if (!tag) return;
    const events = buildDigestDeleteEvents({ activityIds: ids, shapeIds: shapes });
    if (!events.length) return;

    const prior = boardRef.current?.contextRecommendations || {};
    const pruned = pruneFillsForDeletedSources(prior.userProgress, {
      activityIds: ids,
      shapeIds: shapes,
    });
    updateContextRecommendationsLocal({
      ...prior,
      constraints: withdrawConstraintsForDeletedSources(prior.constraints, {
        activityIds: ids,
        shapeIds: shapes,
      }),
      pendingDigest: markDigestDeleted(prior.pendingDigest),
      ...(pruned.pruned ? { userProgress: pruned.userProgress } : {}),
    });

    void (async () => {
      try {
        const result = await postBoardAiDigestEvent(tag, { events });
        if (hashtagRef.current !== tag) return;
        const nextPrior = boardRef.current?.contextRecommendations || {};
        updateContextRecommendationsLocal({
          ...nextPrior,
          constraints: result?.constraints ?? nextPrior.constraints,
          pendingDigest: result?.pendingDigest ?? nextPrior.pendingDigest,
          ...(result?.userProgress ? { userProgress: result.userProgress } : {}),
        });
      } catch (err) {
        console.warn('[digest-event] delete enqueue failed', err?.message || err);
      }
    })();
  }, [updateContextRecommendationsLocal]);

  // Place inbox drafts + digest enqueue + soft-only pin-case (no step revise).
  useEffect(() => {
    if (!hashtag || !boardMatchesRoute || !pinCaseRequest) return undefined;

    const pinCaseHashtag = hashtag;
    const request = pendingPinCaseRef.current || {};
    const runId = request.runId || pinCaseIdRef.current + 1;
    if (request.runId == null) pinCaseIdRef.current = runId;
    const processingTargets = request.processingTargets
      || collectObjectProcessingTargets(request);
    const setProcessingPhase = (phase) => {
      if (!processingTargets.length) return;
      useBoardStore.getState().setObjectProcessing(processingTargets, phase, { runId });
    };
    const isCurrent = () => (
      runId === pinCaseIdRef.current
      && hashtagRef.current === pinCaseHashtag
      && useBoardStore.getState().hashtag === pinCaseHashtag
    );
    const clearThisRun = () => {
      useBoardStore.getState().clearObjectProcessing(processingTargets, { runId });
    };

    let started = false;
    const timer = setTimeout(async () => {
      started = true;
      if (!isCurrent()) {
        clearThisRun();
        return;
      }
      try {
        const currentBoard = boardRef.current;
        const requested = {
          activityIds: Array.isArray(request.activityIds)
            ? request.activityIds.map(String).filter(Boolean)
            : [],
          aiPins: Array.isArray(request.aiPins) ? request.aiPins.filter(Boolean) : [],
          canvasEvidences: Array.isArray(request.canvasEvidences)
            ? request.canvasEvidences.filter(e => e && String(e.text || '').trim())
            : [],
        };
        const placeIds = Array.isArray(request.placeActivityIds)
          ? request.placeActivityIds.map(String).filter(Boolean)
          : [];

        const applyLiveSources = () => filterLivePinCaseSources({
          ...requested,
          editor: editorRef.current,
          activities: useBoardStore.getState().activities || [],
        });
        const dropJudged = sources => dropAlreadyJudgedPinCaseSources({
          ...sources,
          editor: editorRef.current,
          userProgress: boardRef.current?.contextRecommendations?.userProgress,
          placements: boardRef.current?.placements
            || useBoardStore.getState().board?.placements,
        });
        let { activityIds, aiPins, canvasEvidences } = dropJudged(applyLiveSources());

        if (request.placePending !== false && placeIds.length) {
          const inboxIds = listInboxActivityIds(currentBoard);
          const toPlace = placeIds.filter(id => inboxIds.includes(id));
          if (toPlace.length) {
            setProcessingPhase('placing');
            const placed = await postBoardAiPlacePending(pinCaseHashtag, { activityIds: toPlace });
            if (!isCurrent()) return;
            if (Array.isArray(placed?.placements) && placed.placements.length) {
              updatePlacementsLocal(placed.placements);
            }
          }
        }

        if (!activityIds.length && !aiPins.length && !canvasEvidences.length) return;
        if (!isCurrent()) return;

        // Enqueue pendingDigest (fold → constraints at DIGEST_MAX).
        setProcessingPhase('digesting');
        try {
          const storeActivities = useBoardStore.getState().activities || [];
          const digestEvents = buildDigestAddEvents({
            activityIds,
            aiPins,
            canvasEvidences,
            activities: storeActivities,
            editor: editorRef.current,
          });
          if (digestEvents.length) {
            const digestResult = await postBoardAiDigestEvent(pinCaseHashtag, {
              events: digestEvents,
            });
            if (!isCurrent()) return;
            if (digestResult?.ok) {
              const prior = boardRef.current?.contextRecommendations || {};
              updateContextRecommendationsLocal({
                ...prior,
                constraints: digestResult.constraints ?? prior.constraints,
                pendingDigest: digestResult.pendingDigest ?? prior.pendingDigest,
              });
            }
          }
        } catch (digestErr) {
          console.warn('[digest-event] add enqueue failed', digestErr?.message || digestErr);
        }
        if (!isCurrent()) return;

        const live = applyLiveSources();
        if (!hasLivePinCaseSources(live)) {
          const gone = gonePinCaseSources(requested, live);
          if (gone.activityIds.length || gone.shapeIds.length) {
            enqueueDigestDelete(gone.activityIds, gone.shapeIds);
          }
          return;
        }
        ({ activityIds, aiPins, canvasEvidences } = dropJudged(live));
        if (!hasLivePinCaseSources({ activityIds, aiPins, canvasEvidences })) return;

        const editor = editorRef.current;
        let clientContext = null;
        if (editor) {
          try {
            clientContext = await buildClientContext(editor, {
              board: boardRef.current,
              previousContext: previousContextRef.current,
              pinCount: pinCountRef.current,
              recentIntent: boardRef.current?.recentIntent || '',
              locale: localeRef.current,
              includeSnapshot: false,
            });
          } catch {
            clientContext = null;
          }
        }
        if (!isCurrent()) return;

        const coach = liveCoachRef.current;
        const liveCoachPayload = coach
          ? {
            currentStep: readCurrentStep(boardRef.current),
            goal: readCurrentStep(boardRef.current),
            blockers: coach.blockers,
            nextActions: coach.nextActions,
            stepCandidates: stepChoicesFromBoard(boardRef.current),
          }
          : null;
        const payload = {
          softOnly: true,
          ...(liveCoachPayload ? { liveCoach: liveCoachPayload } : {}),
          ...(activityIds.length === 1
            ? { activityId: activityIds[0] }
            : activityIds.length > 1
              ? { activityIds }
              : {}),
          ...(aiPins.length === 1
            ? { aiPin: aiPins[0] }
            : aiPins.length > 1
              ? { aiPins }
              : {}),
          ...(canvasEvidences.length === 1
            ? { canvasEvidence: canvasEvidences[0] }
            : canvasEvidences.length > 1
              ? { canvasEvidences }
              : {}),
        };
        const sourceKinds = [
          activityIds.length > 0,
          aiPins.length > 0,
          canvasEvidences.length > 0,
        ].filter(Boolean).length;
        if (sourceKinds > 1 || activityIds.length + aiPins.length + canvasEvidences.length > 1) {
          if (activityIds.length) {
            payload.activityIds = activityIds;
            delete payload.activityId;
          }
          if (aiPins.length) {
            payload.aiPins = aiPins;
            delete payload.aiPin;
          }
          if (canvasEvidences.length) {
            payload.canvasEvidences = canvasEvidences;
            delete payload.canvasEvidence;
          }
        }

        setProcessingPhase('analyzing');
        const result = await postBoardAiPinCaseAnalyze(pinCaseHashtag, payload, clientContext);
        if (!isCurrent()) return;

        stampCanvasEvidencePinCaseMeta(editor, result);
        applyPinCaseToLocalPlacements(result);

        const liveAfter = applyLiveSources();
        const goneAfter = gonePinCaseSources(requested, liveAfter);
        if (goneAfter.activityIds.length || goneAfter.shapeIds.length) {
          enqueueDigestDelete(goneAfter.activityIds, goneAfter.shapeIds);
        }
        if (!hasLivePinCaseSources(liveAfter)) return;

        // softOnly: never revise steps here.
        if (result?.revised && result?.recommendations) return;

        const softRecs = result?.recommendations;
        const softProgress = result?.softProgress
          || (Array.isArray(result?.results)
            ? result.results.find(r => r?.softProgress)?.softProgress
            : null);
        if (!softRecs || !softProgress) return;

        const normalized = normalizeContextRecommendations(softRecs, fallbackRef.current);
        if (!hasContextRecommendations(normalized) && !softProgress) return;

        // The pin filled a gap of ANOTHER step: point at that step (its pack is
        // already stored on the step) so the user sees the progress.
        const matchedGoal = String(
          softProgress?.matchedGoal || result?.match?.goal || ''
        ).trim();
        const currentGoal = readCurrentStep(boardRef.current).trim();
        if (matchedGoal && matchedGoal.toLowerCase() !== currentGoal.toLowerCase()) {
          const target = findStep(boardRef.current, matchedGoal);
          if (target && stepHasPack(target)) {
            await useBoardStore.getState().setCurrentStep({ stepId: target.id });
            if (!isCurrent()) return;
          }
        }

        // Keep the live step pack; only patch gap progress.
        const applied = applySoftProgressToLiveCoach(
          liveCoachRef.current,
          normalized,
          softProgress
        ) || normalized;
        if (!hasContextRecommendations(applied)) return;

        const priorRecs = boardRef.current?.contextRecommendations || {};
        const withMeta = withContextRecommendationMeta(applied, {
          pinCount: pinCountRef.current,
          aiPinCount: countAiPinsFromEditor(editorRef.current),
          frameCount: countFramesFromEditor(editorRef.current, boardRef.current),
          currentStep: readCurrentStep(boardRef.current) || matchedGoal,
          boardGoal: readBoardGoal(boardRef.current),
          recentIntent: boardRef.current?.recentIntent || '',
          locale: localeRef.current,
        });
        updateContextRecommendationsLocal({
          ...withMeta,
          constraints: priorRecs.constraints,
          pendingDigest: priorRecs.pendingDigest,
        });

        // All gaps closed by soft fills → finish the step; server advances the pointer.
        const stepNow = selectCurrentStep(useBoardStore.getState().board);
        if (stepNow && resolveGoalStepStatus(applied) === 'finished') {
          await useBoardStore.getState().updateStep(stepNow.id, {
            status: 'finished',
            achievementRate: 100,
            finishedBy: 'board',
          });
          if (!isCurrent()) return;
        }

        // Mark pasted evidence shapes with doneKey / partialKey meta.
        if (editor) {
          const shapeMetaUpdates = [];
          const analyzeCost = normalizeAiCost(result?.jobMetrics);
          const recordShapeMeta = (shapeId, key, field) => {
            const id = String(shapeId || '').trim();
            const value = String(key || '').trim();
            if (!id || !value) return;
            const shape = editor.getShape(id);
            if (!shape) return;
            shapeMetaUpdates.push({
              id,
              type: shape.type,
              meta: {
                ...(shape.meta || {}),
                [field]: value,
                ...mergeAiCostIntoMeta(shape.meta || {}, analyzeCost),
              },
            });
          };
          const rows = Array.isArray(result?.results) ? result.results : [{ softProgress }];
          for (const entry of rows) {
            const sp = entry?.softProgress;
            if (!sp) continue;
            const shapeId = sp.matchedShapeId || entry?.pinSummary?.shapeId;
            if (sp.matchedKind === 'partial') recordShapeMeta(shapeId, sp.matchedKey, 'partialKey');
            else recordShapeMeta(shapeId, sp.matchedKey, 'doneKey');
          }
          if (shapeMetaUpdates.length) {
            try {
              editor.updateShapes(shapeMetaUpdates);
            } catch {
              // meta is best-effort
            }
          }
        }

        // Done user pins go in a frame named for the missing part.
        const doneFills = applied?.userProgress?.doneFills || softProgress?.doneFills || [];
        const fillTargets = [];
        const rows = Array.isArray(result?.results)
          ? result.results
          : [{ softProgress, match: result?.match }];
        for (const entry of rows) {
          const sp = entry?.softProgress;
          if (!sp || sp.matchedKind === 'partial') continue;
          const aid = String(sp.matchedActivityId || entry?.pinSummary?.activityId || '').trim();
          const shapeId = String(sp.matchedShapeId || entry?.pinSummary?.shapeId || '').trim();
          const key = String(sp.matchedKey || '').trim();
          if (!shouldFrameDoneFill({
            matchedActivityId: aid,
            matchedShapeId: shapeId,
            matchedKey: key,
          })) continue;
          const title = String(entry?.match?.title || key).trim();
          fillTargets.push({ activityId: aid, doneKey: key, frameLabel: title });
        }
        if (fillTargets.length && editor) {
          setProcessingPhase('locating');
          const storeState = useBoardStore.getState();
          const saveSnapshot = createOpsOnlyDocumentSaver(editor);
          for (const target of fillTargets) {
            placeDonePinInGapFrame(editor, target.activityId, doneFills, {
              doneKey: target.doneKey,
              frameLabel: target.frameLabel,
              activities: storeState.activities,
              board: storeState.board,
              updatePlacementsLocal: storeState.updatePlacementsLocal,
              savePlacements: storeState.savePlacements,
              saveFrames: storeState.saveFrames,
              saveSnapshot,
            });
          }
        }

        if (result?.jobMetrics) {
          setLastJobMetrics({
            ...result.jobMetrics,
            cached: Boolean(result.cached || result.jobMetrics.cached),
          });
        }
      } catch (err) {
        if (!isCurrent()) return;
        console.warn('[pin-case]', err?.message || err);
      } finally {
        clearThisRun();
        if (isCurrent()) pendingPinCaseRef.current = null;
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if (!started) clearThisRun();
    };
  }, [
    hashtag,
    boardMatchesRoute,
    pinCaseRequest,
    editorRef,
    updatePlacementsLocal,
    savePlacements,
    updateContextRecommendationsLocal,
    enqueueDigestDelete,
  ]);

  // ---- Small local coach edits ---------------------------------------------
  const showMoreSuggestions = useCallback(() => {
    setSuggestionBatchIndex(index => {
      const batchCount = getContextRecommendationBatchCount(liveCoachRef.current);
      if (batchCount <= 1) return 0;
      return (index + 1) % batchCount;
    });
  }, []);

  /** Fold a gap (not-)interested mark into recentIntent. No AI call. */
  const recordGapInterest = useCallback((gapTitle, { interested = false } = {}) => {
    const label = String(gapTitle || '').trim();
    if (!label) return;
    pushBoardIntentEvent(hashtagRef.current, {
      type: interested
        ? INTENT_EVENT_TYPES.GAP_INTERESTED
        : INTENT_EVENT_TYPES.GAP_NOT_INTERESTED,
      label,
    });
    updateRecentIntentLocal(foldBoardIntentSummary(hashtagRef.current, {
      priorSummary: boardRef.current?.recentIntent || '',
    }));
  }, [updateRecentIntentLocal]);

  /** Persist a Decide answer: recentIntent + a constraints-ledger decision row. */
  const recordUserDecision = useCallback(async ({
    question = '',
    answer = '',
    blockerTitle = '',
    sourceShapeId = '',
  } = {}) => {
    const choice = String(answer || '').trim();
    if (!choice) return;
    pushBoardIntentEvent(hashtagRef.current, {
      type: INTENT_EVENT_TYPES.DECIDED,
      label: choice,
    });
    const nextIntent = foldBoardIntentSummary(hashtagRef.current, {
      priorSummary: boardRef.current?.recentIntent || '',
    });
    updateRecentIntentLocal(nextIntent);

    const incoming = buildUserDecisionConstraint({
      question,
      answer: choice,
      blockerTitle,
      sourceShapeId,
    });
    const prior = boardRef.current?.contextRecommendations;
    if (!incoming || !prior || typeof prior !== 'object') return;
    const nextRecs = {
      ...prior,
      constraints: mergeUserDecisionConstraint(prior.constraints, incoming),
    };
    try {
      await persistContextRecommendations(nextRecs);
    } catch (err) {
      console.warn('[decide] persist decision constraint failed', err?.message || err);
    }
  }, [persistContextRecommendations, updateRecentIntentLocal]);

  /** Remove a finished Do-next job locally so it no longer shows. */
  const dismissNextAction = useCallback((action) => {
    if (!action) return;
    const dismissKey = actionKeyForNextAction(action);
    const dismissLabel = String(action.label || '').trim().toLowerCase();
    pushBoardIntentEvent(hashtagRef.current, {
      type: INTENT_EVENT_TYPES.REC_DISMISSED,
      label: action.label || action.kind || 'suggestion',
    });
    const nextIntent = foldBoardIntentSummary(hashtagRef.current, {
      priorSummary: boardRef.current?.recentIntent || '',
    });
    updateRecentIntentLocal(nextIntent);

    const prev = boardRef.current?.contextRecommendations;
    if (!prev?.nextActions?.length) return;
    const nextActions = prev.nextActions.filter((item, index) => {
      const itemKey = actionKeyForNextAction(item, index);
      if (dismissKey && itemKey === dismissKey) return false;
      if (dismissLabel && String(item.label || '').trim().toLowerCase() === dismissLabel) {
        return false;
      }
      return true;
    });
    if (nextActions.length === prev.nextActions.length) return;
    const nextBlockers = Array.isArray(prev.blockers)
      ? prev.blockers.filter((blocker) => {
        const title = String(blocker?.title || '').trim().toLowerCase();
        const linked = String(action.blockerTitle || '').trim().toLowerCase();
        return !(linked && title === linked);
      })
      : prev.blockers;
    updateContextRecommendationsLocal({
      ...prev,
      nextActions,
      blockers: nextBlockers,
      gaps: Array.isArray(nextBlockers) && nextBlockers.length
        ? nextBlockers.map(b => b.title).filter(Boolean).slice(0, 5)
        : prev.gaps,
    });
  }, [updateContextRecommendationsLocal, updateRecentIntentLocal]);

  return {
    contextActions,
    contextLoading,
    contextError,
    thinkingPhase,
    lastJobMetrics,
    refreshContextActions,
    analyzePinAdded,
    enqueueDigestDelete,
    showMoreSuggestions,
    canShowMoreSuggestions,
    suggestionBatchIndex,
    suggestionBatchCount,
    dismissNextAction,
    recordGapInterest,
    recordUserDecision,
    fallback,
  };
}
